import { channelsToKeyArray } from './channels';
import { readSubscriberProfileFields } from './subscriberProfileContext';
import { subscriberSource, subscriberSourceLabel } from './subscriberSource';
import type { SubscriberRow } from '../components/Subscribers/types';

export type MockSemanticMatch = {
  row: SubscriberRow;
  similarity: number;
};

export type MockSelectionInsights = {
  count: number;
  sharedChannels: string[];
  roleCounts: Record<string, number>;
  sourceCounts: Record<string, number>;
  commonKeywords: string[];
  summary: string;
};

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does',
  'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'can',
  'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'my',
  'your', 'our', 'their', 'not', 'no', 'yes', 'so', 'if', 'as', 'from', 'about',
]);

function rowSearchText(row: SubscriberRow): string {
  const profile = readSubscriberProfileFields(row);
  const parts = [
    row.email,
    row.name,
    row.role,
    profile.icp,
    profile.callMeWhen1,
    profile.callMeWhen2,
    profile.callMeWhen3,
    profile.linkedin_url,
    subscriberSource(row),
    JSON.stringify(row.custom_data ?? {}),
  ];
  return parts.filter((p) => typeof p === 'string' && p.trim()).join(' ').toLowerCase();
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 && !STOP_WORDS.has(t));
}

/** Client-side mock semantic filter — token overlap, no embeddings. */
export function mockSemanticFilter(rows: SubscriberRow[], query: string, limit = 50): MockSemanticMatch[] {
  const qTokens = tokenize(query.trim());
  if (qTokens.length === 0) return [];

  const scored: MockSemanticMatch[] = [];
  for (const row of rows) {
    const text = rowSearchText(row);
    const rowTokens = new Set(tokenize(text));
    if (rowTokens.size === 0) continue;

    let hits = 0;
    for (const t of qTokens) {
      if (rowTokens.has(t)) hits += 1;
      else if (text.includes(t)) hits += 0.5;
    }
    if (hits === 0) continue;

    const similarity = Math.min(99, Math.round((hits / qTokens.length) * 100));
    scored.push({ row, similarity });
  }

  scored.sort((a, b) => b.similarity - a.similarity);
  return scored.slice(0, limit);
}

function countBy<T>(items: T[], keyFn: (item: T) => string | null | undefined): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) {
    const k = keyFn(item)?.trim();
    if (!k) continue;
    counts[k] = (counts[k] ?? 0) + 1;
  }
  return counts;
}

/** Mock bulk commonality insights for selected subscribers. */
export function mockSelectionInsights(rows: SubscriberRow[]): MockSelectionInsights {
  const count = rows.length;
  if (count === 0) {
    return {
      count: 0,
      sharedChannels: [],
      roleCounts: {},
      sourceCounts: {},
      commonKeywords: [],
      summary: 'No subscribers selected.',
    };
  }

  const channelSets = rows.map((r) => new Set(channelsToKeyArray(r.channels)));
  let sharedChannels: string[] = [];
  if (channelSets.length > 0) {
    sharedChannels = [...channelSets[0]!].filter((ch) =>
      channelSets.every((s) => s.has(ch)),
    );
  }

  const roleCounts = countBy(rows, (r) => (typeof r.role === 'string' ? r.role : null));
  const sourceCounts = countBy(rows, (r) => {
    const s = subscriberSource(r);
    return s ? subscriberSourceLabel(s) : null;
  });

  const keywordFreq: Record<string, number> = {};
  for (const row of rows) {
    const profile = readSubscriberProfileFields(row);
    const blob = [profile.icp, profile.callMeWhen1, profile.callMeWhen2, profile.callMeWhen3].filter(Boolean).join(' ');
    for (const token of tokenize(blob)) {
      keywordFreq[token] = (keywordFreq[token] ?? 0) + 1;
    }
  }
  const commonKeywords = Object.entries(keywordFreq)
    .filter(([, n]) => n >= Math.max(2, Math.ceil(count * 0.4)))
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([word]) => word);

  const parts: string[] = [];
  parts.push(`Analyzed ${count} subscriber${count === 1 ? '' : 's'}.`);
  if (sharedChannels.length > 0) {
    parts.push(`All share channel${sharedChannels.length === 1 ? '' : 's'}: ${sharedChannels.join(', ')}.`);
  }
  const topRole = Object.entries(roleCounts).sort((a, b) => b[1] - a[1])[0];
  if (topRole && topRole[1] > 1) {
    parts.push(`Most common role: ${topRole[0]} (${topRole[1]} of ${count}).`);
  }
  if (commonKeywords.length > 0) {
    parts.push(`Recurring themes in ICP / call-me-when: ${commonKeywords.join(', ')}.`);
  }
  if (parts.length === 1) {
    parts.push('No strong overlap detected in channels, roles, or profile text.');
  }

  return {
    count,
    sharedChannels,
    roleCounts,
    sourceCounts,
    commonKeywords,
    summary: parts.join(' '),
  };
}

export type MockNewsletterSuggestion = {
  chosen_index: number;
  article_id: string;
  title: string;
  subtitle: string;
  web_url: string;
  reason: string;
  suggested_sentence: string;
};

/** Mock article suggestion — picks first post, no AI edge fn. */
export function mockNewsletterArticleSuggestion(
  posts: { id: string; title: string; subtitle: string; web_url: string }[],
): MockNewsletterSuggestion | null {
  const post = posts[0];
  if (!post) return null;
  return {
    chosen_index: 0,
    article_id: post.id,
    title: post.title,
    subtitle: post.subtitle,
    web_url: post.web_url,
    reason: 'Mock suggestion — first recent post (embeddings / AI coming later).',
    suggested_sentence: `Given our recent conversations, you might find "${post.title}" useful.`,
  };
}

/** Mock email generation from subscriber profile. */
export function mockGenerateEmail(
  profile: ReturnType<typeof readSubscriberProfileFields>,
  userPrompt: string,
): string {
  const name = profile.name?.trim() || 'there';
  const subject = `Follow-up: ${profile.name?.trim() || 'our conversation'}`;
  const body = `Hi ${name},

${userPrompt.trim()}

${profile.icp ? `Context — ICP: ${profile.icp}` : ''}

Best,
[Your name]

---
(Mock AI — real generation will use embeddings / LLM later)`;
  return `Subject: ${subject}\n\n${body}`;
}
